import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';

export const ManageAgentsPage = () => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data
  const agents = [
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah@sterlingliterary.com',
      title: 'Senior Agent',
      photo: '👩‍💼',
      status: 'active',
      joinDate: '2015-06-01',
      genres: ['Literary Fiction', 'Thriller'],
      stats: {
        activeQueries: 43,
        requestedMSS: 8,
        activeClients: 24,
        dealsThisYear: 5,
      },
      queryStatus: 'open',
    },
    {
      id: 2,
      name: 'Michael Chen',
      email: 'michael@sterlingliterary.com',
      title: 'Agent',
      photo: '👨‍💼',
      status: 'active',
      joinDate: '2017-03-15',
      genres: ['Science Fiction', 'Fantasy'],
      stats: {
        activeQueries: 38,
        requestedMSS: 5,
        activeClients: 18,
        dealsThisYear: 3,
      },
      queryStatus: 'closed',
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      email: 'emily@sterlingliterary.com',
      title: 'Agent',
      photo: '👩‍💼',
      status: 'active',
      joinDate: '2018-01-10',
      genres: ['Romance', 'Women\'s Fiction'],
      stats: {
        activeQueries: 52,
        requestedMSS: 12,
        activeClients: 22,
        dealsThisYear: 7,
      },
      queryStatus: 'open',
    },
    {
      id: 4,
      name: 'David Park',
      email: 'david@sterlingliterary.com',
      title: 'Agent',
      photo: '👨‍💼',
      status: 'active',
      joinDate: '2019-09-01',
      genres: ['Mystery', 'Thriller'],
      stats: {
        activeQueries: 29,
        requestedMSS: 4,
        activeClients: 12,
        dealsThisYear: 2,
      },
      queryStatus: 'open',
    },
    {
      id: 5,
      name: 'Jennifer Williams',
      email: 'jennifer@sterlingliterary.com',
      title: 'Junior Agent',
      photo: '👩‍💼',
      status: 'active',
      joinDate: '2022-05-15',
      genres: ['YA Fantasy', 'Contemporary'],
      stats: {
        activeQueries: 31,
        requestedMSS: 3,
        activeClients: 8,
        dealsThisYear: 1,
      },
      queryStatus: 'open',
    },
    {
      id: 6,
      name: 'Robert Taylor',
      email: 'robert@sterlingliterary.com',
      title: 'Agent',
      photo: '👨‍💼',
      status: 'inactive',
      joinDate: '2016-11-20',
      genres: ['Historical Fiction', 'Literary Fiction'],
      stats: {
        activeQueries: 0,
        requestedMSS: 0,
        activeClients: 15,
        dealsThisYear: 0,
      },
      queryStatus: 'closed',
    },
  ];

  const pendingInvites = [
    {
      id: 1,
      name: 'Alex Martinez',
      email: 'alex.martinez@email.com',
      invitedDate: '2024-02-28',
      invitedBy: 'Sarah Johnson',
    },
  ];

  const handleSendInvite = () => {
    if (!inviteEmail.trim() || !inviteName.trim()) {
      window.alert('Please fill in all fields');
      return;
    }

    console.log('Sending invite:', { name: inviteName, email: inviteEmail });
    window.alert(`Invitation sent to ${inviteName} (${inviteEmail})!`);
    setShowInviteForm(false);
    setInviteEmail('');
    setInviteName('');
  };

  const handleRemoveAgent = (agentName: string) => {
    if (window.confirm(`Are you sure you want to remove ${agentName} from the agency? This action cannot be undone.`)) {
      console.log('Removing agent:', agentName);
      window.alert(`${agentName} has been removed from the agency.`);
    }
  };

  const handleToggleStatus = (agentName: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    console.log(`Changing ${agentName} status to ${newStatus}`);
    window.alert(`${agentName} status changed to ${newStatus}`);
  };

  const handleCancelInvite = (email: string) => {
    if (window.confirm(`Cancel invitation to ${email}?`)) {
      console.log('Canceling invite:', email);
      window.alert(`Invitation to ${email} has been canceled.`);
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = agents.filter(a => a.status === 'active').length;
  const totalClients = agents.reduce((sum, agent) => sum + agent.stats.activeClients, 0);
  const totalDeals = agents.reduce((sum, agent) => sum + agent.stats.dealsThisYear, 0);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '32px',
        }}>
          <div>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Manage Agents
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              {activeCount} active agents • {totalClients} total clients • {totalDeals} deals this year
            </p>
          </div>

          <Button onClick={() => setShowInviteForm(true)}>
            + Invite Agent
          </Button>
        </div>

        {/* Invite Form */}
        {showInviteForm && (
          <Card style={{
            marginBottom: '24px',
            backgroundColor: theme.colors.primaryLight,
            border: `2px solid ${theme.colors.primary}`,
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '16px',
            }}>
              Invite New Agent
            </h3>

            <Input
              label="Full Name *"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Agent's full name"
              style={{ marginBottom: '16px' }}
            />

            <Input
              label="Email Address *"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="agent@email.com"
              style={{ marginBottom: '16px' }}
            />

            <div style={{
              padding: '12px',
              backgroundColor: theme.colors.white,
              borderRadius: theme.borderRadius.small,
              fontSize: '13px',
              color: theme.colors.textSecondary,
              marginBottom: '16px',
            }}>
              ℹ️ An invitation email will be sent with instructions to create an account and join the agency.
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowInviteForm(false);
                  setInviteEmail('');
                  setInviteName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendInvite}
                disabled={!inviteEmail.trim() || !inviteName.trim()}
              >
                Send Invitation
              </Button>
            </div>
          </Card>
        )}

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <Card style={{ marginBottom: '24px', backgroundColor: theme.colors.warningLight }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '16px',
            }}>
              Pending Invitations ({pendingInvites.length})
            </h3>

            {pendingInvites.map((invite) => (
              <div
                key={invite.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '8px',
                }}
              >
                <div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '4px',
                  }}>
                    {invite.name}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                  }}>
                    {invite.email} • Invited by {invite.invitedBy} on {new Date(invite.invitedDate).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => window.alert('Resend invite functionality')}
                  >
                    Resend
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleCancelInvite(invite.email)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Search */}
        <Card style={{ marginBottom: '24px' }}>
          <Input
            label="Search Agents"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Card>

        {/* Agent List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredAgents.map((agent) => (
            <Card key={agent.id}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
                <div style={{ display: 'flex', gap: '20px', flex: 1 }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.backgroundAlt,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px',
                    flexShrink: 0,
                  }}>
                    {agent.photo}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                    }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        margin: 0,
                      }}>
                        {agent.name}
                      </h3>
                      <Badge variant={agent.status === 'active' ? 'success' : 'default'}>
                        {agent.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant={agent.queryStatus === 'open' ? 'primary' : 'default'}>
                        {agent.queryStatus === 'open' ? 'Open to Queries' : 'Closed to Queries'}
                      </Badge>
                    </div>

                    <div style={{
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '8px',
                    }}>
                      {agent.title} • {agent.email}
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      marginBottom: '16px',
                    }}>
                      {agent.genres.map((genre) => (
                        <Badge key={genre} variant="default">
                          {genre}
                        </Badge>
                      ))}
                    </div>

                    {/* Stats Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '16px',
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.medium,
                    }}>
                      <div>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: '500',
                          color: theme.colors.primary,
                        }}>
                          {agent.stats.activeQueries}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                        }}>
                          Active Queries
                        </div>
                      </div>
                      <div>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: '500',
                          color: theme.colors.info,
                        }}>
                          {agent.stats.requestedMSS}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                        }}>
                          Requested MSS
                        </div>
                      </div>
                      <div>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: '500',
                          color: theme.colors.success,
                        }}>
                          {agent.stats.activeClients}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                        }}>
                          Active Clients
                        </div>
                      </div>
                      <div>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: '500',
                          color: theme.colors.warning,
                        }}>
                          {agent.stats.dealsThisYear}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                        }}>
                          Deals This Year
                        </div>
                      </div>
                    </div>

                    <div style={{
                      marginTop: '12px',
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                    }}>
                      Joined {new Date(agent.joinDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginLeft: '20px',
                }}>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => window.location.href = `/agents/${agent.id}`}
                  >
                    View Profile
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => handleToggleStatus(agent.name, agent.status)}
                  >
                    {agent.status === 'active' ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleRemoveAgent(agent.name)}
                    style={{ color: theme.colors.error }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredAgents.length === 0 && (
          <Card style={{ textAlign: 'center', padding: '60px 40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              🔍
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '8px',
            }}>
              No agents found
            </h3>
            <p style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
            }}>
              Try adjusting your search
            </p>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};
